import * as Sentry from '@sentry/react';

export type SandboxAgentErrorPhase =
  | 'history_load'
  | 'sdk_transport_error'
  | 'stream_parse_error'
  | 'stream_terminal_error'
  | 'poll_timeout'
  | 'poll_auth'
  | 'send_message';

export type SandboxAgentTransport = 'http' | 'sdk' | 'sse' | 'polling';

export type SandboxAgentTelemetryInput = {
  phase: SandboxAgentErrorPhase;
  transport: SandboxAgentTransport;
  instanceId: string | null;
  status?: string;
  messageCount?: number;
  lastMessageId?: string;
  lastMessageRole?: string;
  historyLoadedFor?: string | null;
  pollAttempt?: number;
  maxPollAttempts?: number;
  elapsedMs?: number;
  usingPolling?: boolean;
  lastHistoryMessageId?: string;
  lastHistoryMessageRole?: string;
  lastHistoryCount?: number;
  originalErrorMessage?: string;
};

export type SandboxAgentTelemetryContext = SandboxAgentTelemetryInput & {
  errorMessage: string;
};

export function normalizeSandboxAgentError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

export function buildSandboxAgentTelemetryContext(
  error: Error,
  input: SandboxAgentTelemetryInput
): SandboxAgentTelemetryContext {
  return {
    ...input,
    errorMessage: error.message,
  };
}

export function captureSandboxAgentError(
  errorInput: unknown,
  input: SandboxAgentTelemetryInput
): Error {
  const error = normalizeSandboxAgentError(errorInput);
  const context = buildSandboxAgentTelemetryContext(error, input);

  Sentry.withScope((scope) => {
    scope.setFingerprint(['sandbox-agent', context.phase, context.errorMessage]);
    scope.setTag('source', 'sandbox-agent');
    scope.setTag('sandbox_agent.phase', context.phase);
    scope.setTag('sandbox_agent.transport', context.transport);
    scope.setTag('sandbox_agent.instance_id', context.instanceId ?? 'none');

    if (context.pollAttempt !== undefined) {
      scope.setTag('sandbox_agent.poll_attempt', String(context.pollAttempt));
    }
    if (context.maxPollAttempts !== undefined) {
      scope.setTag('sandbox_agent.max_poll_attempts', String(context.maxPollAttempts));
    }
    if (context.lastMessageRole) {
      scope.setTag('sandbox_agent.last_message_role', context.lastMessageRole);
    }
    if (context.lastHistoryMessageRole) {
      scope.setTag('sandbox_agent.last_history_message_role', context.lastHistoryMessageRole);
    }

    scope.setContext('sandbox_agent', context);
    Sentry.captureException(error);
  });

  return error;
}

export function addSandboxAgentBreadcrumb(
  message: string,
  data: Record<string, unknown>,
  level: 'info' | 'warning' | 'error' = 'info'
) {
  Sentry.addBreadcrumb({
    category: 'sandbox-agent',
    message,
    level,
    data,
  });
}
